// Block-model shapes — mirror the backend Pydantic models (panels/blocks.py).
// Kept in a component-free module so the block forms/inspectors (config-forms.tsx,
// blocks/*) export only components (satisfies react-refresh/only-export-components).
// panels.ts (the API client) re-uses Block/PanelStyle from here to type Panel.config.

// ── leaf shapes ─────────────────────────────────────────────────────────────

export type FieldColumn = { field_id: string; label?: string }

export type DisplayKind =
  | 'auto'
  | 'status'
  | 'priority'
  | 'user'
  | 'date'
  | 'labels'
  | 'number'
  | 'text'
export type DateFormat = 'relative' | 'dmy' | 'with_time' | 'iso'
export type UserMode = 'avatar_name' | 'name' | 'email' | 'mention'
export type StatusStyle = 'lozenge' | 'text'
export type PriorityMode = 'icon_name' | 'icon' | 'name'
export type LabelsStyle = 'chips' | 'list'

/** Typed display options — every field has a default (mirrors DisplaySpec). */
export type DisplaySpec = {
  kind: DisplayKind
  date_format: DateFormat
  overdue: boolean
  user_mode: UserMode
  status_style: StatusStyle
  priority_mode: PriorityMode
  labels_style: LabelsStyle
}

export type FieldSource = 'self' | 'parent' | 'child'
export type Aggregate = 'sum' | 'count' | 'list' | 'min' | 'max'

export type BambooRole = 'assignee' | 'reporter'
export type BambooField =
  | 'job_title'
  | 'department'
  | 'division'
  | 'finproject'
  | 'supervisor'
  | 'status'

/** One row in an issue_fields block: id + source + (child→aggregate) + display. */
export type FieldSpec = {
  field_id: string
  label?: string
  source: FieldSource
  aggregate?: Aggregate
  display?: DisplaySpec
}

export type ChecklistStep = { label: string; jql_template: string; done_categories: string[] }

// ── blocks (discriminated by `type`) ────────────────────────────────────────

export type HeadingBlock = { type: 'heading'; text: string }
export type DividerBlock = { type: 'divider' }
export type IssueFieldsBlock = { type: 'issue_fields'; fields: FieldSpec[] }
export type JqlTableBlock = {
  type: 'jql_table'
  jql_template: string
  columns: FieldColumn[]
  max_rows: number
  order_by?: string
  // optional: adds a leading "#" column — useful on long tables, noise on short ones
  show_row_numbers?: boolean
}
export type ProgressChecklistBlock = { type: 'progress_checklist'; steps: ChecklistStep[] }
export type CustomHtmlBlock = { type: 'custom_html'; html: string }
export type SlackDmButtonBlock = {
  type: 'slack_dm_button'
  label: string
  default_message: string
  issue_type_contains: string
  preselect_reporter: boolean
}
export type FinRenewalButtonBlock = {
  type: 'fin_renewal_button'
  scenario: 'host_fin' | 'dom_fin' | 'dom_finorg' | 'dom_auto'
  label: string
  issue_type_contains: string
  // dom_auto: organizations routed to FIN; any other value routes to FINORG.
  fin_organizations: string[]
  finorg_label: string
  organization_field_id: string
}
export type CreateIssueRuleSource = 'copy' | 'const' | 'template' | 'date_offset'
export type CreateIssueFieldRule = {
  target: string
  source: CreateIssueRuleSource
  from_field: string
  // const: a string, or accountIds for user fields
  value: string | string[] | null
  prefix: string
  days: number
  working_days: boolean
}
export type CreateIssueButtonBlock = {
  type: 'create_issue_button'
  label: string
  // exact issue-type NAMES of the source issue the button shows on
  issue_types: string[]
  target_project_id: string
  target_issue_type_id: string
  fields: CreateIssueFieldRule[]
  // digits, or '' = no link
  link_type_id: string
  link_direction: 'source_outward' | 'source_inward'
}
export type BambooProfileBlock = { type: 'bamboo_profile'; show: BambooRole[]; fields: BambooField[] }

export type Block =
  | HeadingBlock
  | DividerBlock
  | IssueFieldsBlock
  | JqlTableBlock
  | ProgressChecklistBlock
  | CustomHtmlBlock
  | SlackDmButtonBlock
  | FinRenewalButtonBlock
  | CreateIssueButtonBlock
  | BambooProfileBlock

export type BlockType = Block['type']

// ── panel-level style (stored as data, mirrors PanelStyle) ──────────────────

export type PanelAccent = 'fields' | 'jql' | 'check' | 'html' | 'neutral'
export type PanelStyle = {
  accent: PanelAccent
  density: 'compact' | 'cozy'
  show_labels: boolean
  theme: 'match' | 'light' | 'dark'
}

export type PanelModel = { blocks: Block[]; style: PanelStyle }

// ── defaults & factories ────────────────────────────────────────────────────

export const DEFAULT_DISPLAY: DisplaySpec = {
  kind: 'auto',
  date_format: 'relative',
  overdue: false,
  user_mode: 'avatar_name',
  status_style: 'lozenge',
  priority_mode: 'icon_name',
  labels_style: 'chips',
}

export const DEFAULT_STYLE: PanelStyle = {
  accent: 'fields',
  density: 'compact',
  show_labels: true,
  theme: 'match',
}

/** Add-block menu order (mockup: fields · bamboo · jql · checklist · html · slack dm · heading · divider). */
export const BLOCK_TYPES: BlockType[] = [
  'issue_fields',
  'bamboo_profile',
  'jql_table',
  'progress_checklist',
  'custom_html',
  'slack_dm_button',
  'fin_renewal_button',
  'create_issue_button',
  'heading',
  'divider',
]

export function newCreateIssueRule(): CreateIssueFieldRule {
  return {
    target: '',
    source: 'copy',
    from_field: '',
    value: null,
    prefix: '',
    days: 1,
    working_days: true,
  }
}

export function newField(): FieldSpec {
  return { field_id: '', source: 'self' }
}

/** Fresh block with per-type default body — used when adding a block. */
export function newBlock(type: BlockType): Block {
  switch (type) {
    case 'heading':
      return { type: 'heading', text: '' }
    case 'divider':
      return { type: 'divider' }
    case 'issue_fields':
      return { type: 'issue_fields', fields: [newField()] }
    case 'bamboo_profile':
      return {
        type: 'bamboo_profile',
        show: ['assignee', 'reporter'],
        fields: ['job_title', 'department', 'division', 'finproject'],
      }
    case 'jql_table':
      return {
        type: 'jql_table',
        jql_template: '',
        columns: [{ field_id: '' }],
        max_rows: 20,
        order_by: '',
        show_row_numbers: false,
      }
    case 'progress_checklist':
      return { type: 'progress_checklist', steps: [{ label: '', jql_template: '', done_categories: ['done'] }] }
    case 'custom_html':
      return { type: 'custom_html', html: '' }
    case 'slack_dm_button':
      return {
        type: 'slack_dm_button',
        label: 'Slack msg',
        // Verbatim mirror of backend default (panels/blocks.py SlackDmButtonBlock) — do not retype/retranslate.
        default_message:
          'Привіт! Будь ласка, зверни увагу, для продовження роботи над цією задачею очікуємо дію з твого боку',
        issue_type_contains: '',
        preselect_reporter: true,
      }
    case 'fin_renewal_button':
      return {
        type: 'fin_renewal_button',
        scenario: 'host_fin',
        // Verbatim mirror of backend defaults (panels/blocks.py FinRenewalButtonBlock).
        label: 'Створити Renewal',
        issue_type_contains: '',
        fin_organizations: ['Boosta'],
        finorg_label: 'Створити FINORG Renewal',
        organization_field_id: 'customfield_10286',
      }
    case 'create_issue_button':
      return {
        type: 'create_issue_button',
        label: 'Створити задачу',
        issue_types: ['Task'],
        target_project_id: '',
        target_issue_type_id: '',
        fields: [],
        link_type_id: '',
        link_direction: 'source_outward',
      }
  }
}

/** One-line summary shown next to a block's type badge in the list. */
export function blockSummary(b: Block): string {
  switch (b.type) {
    case 'heading':
      return b.text.trim() || '(no text)'
    case 'divider':
      return '—'
    case 'issue_fields': {
      const ids = b.fields.map((f) => f.field_id.trim()).filter(Boolean)
      return ids.length ? ids.slice(0, 4).join(' · ') + (ids.length > 4 ? ` +${ids.length - 4}` : '') : '(no fields)'
    }
    case 'jql_table':
      return b.jql_template.trim() || '(no jql)'
    case 'progress_checklist': {
      const labels = b.steps.map((s) => s.label.trim()).filter(Boolean)
      return labels.length ? `${labels.length} step${labels.length > 1 ? 's' : ''} · ${labels[0]}` : '(no steps)'
    }
    case 'custom_html':
      return b.html.trim() ? `${b.html.trim().length} chars` : '(empty)'
    case 'slack_dm_button':
      return b.issue_type_contains.trim() ? `${b.label} · type~"${b.issue_type_contains.trim()}"` : b.label
    case 'fin_renewal_button':
      return b.issue_type_contains.trim()
        ? `${b.scenario} · type~"${b.issue_type_contains.trim()}"`
        : b.scenario
    case 'create_issue_button':
      return `${b.label} · ${b.fields.length} rule${b.fields.length === 1 ? '' : 's'}`
    case 'bamboo_profile':
      return `${b.show.join(', ')} · ${b.fields.length} field${b.fields.length > 1 ? 's' : ''}`
  }
}

// ── serialization: strip empties so the payload matches backend expectations ─

function cleanColumn(p: FieldColumn): FieldColumn {
  const label = p.label?.trim()
  return label ? { field_id: p.field_id.trim(), label } : { field_id: p.field_id.trim() }
}

function cleanField(f: FieldSpec): FieldSpec {
  const out: FieldSpec = { field_id: f.field_id.trim(), source: f.source }
  const label = f.label?.trim()
  if (label) out.label = label
  if (f.source === 'child') out.aggregate = f.aggregate ?? 'sum'
  // `auto` == the backend default; drop the object so stored config stays lean.
  if (f.display && f.display.kind !== 'auto') out.display = { ...f.display }
  return out
}

// Keep only what the rule's source reads; the backend ignores the rest but stored config stays lean.
function cleanRule(r: CreateIssueFieldRule): CreateIssueFieldRule {
  const out: CreateIssueFieldRule = {
    target: r.target.trim(),
    source: r.source,
    from_field: '',
    value: null,
    prefix: '',
    days: 1,
    working_days: true,
  }
  if (r.source === 'copy') out.from_field = r.from_field.trim()
  else if (r.source === 'template') {
    out.from_field = r.from_field.trim()
    out.prefix = r.prefix
  } else if (r.source === 'const') {
    out.value = Array.isArray(r.value)
      ? r.value.map((v) => v.trim()).filter(Boolean)
      : (r.value ?? '').trim()
  } else {
    out.days = r.days
    out.working_days = r.working_days
  }
  return out
}

/** Strip empty rows/blanks from every block for the save payload. */
export function cleanBlocks(blocks: Block[]): Block[] {
  return blocks.map((b): Block => {
    switch (b.type) {
      case 'heading':
        return { type: 'heading', text: b.text.trim() }
      case 'divider':
        return { type: 'divider' }
      case 'issue_fields':
        return {
          type: 'issue_fields',
          fields: b.fields.filter((f) => f.field_id.trim()).map(cleanField),
        }
      case 'jql_table': {
        const out: JqlTableBlock = {
          type: 'jql_table',
          jql_template: b.jql_template.trim(),
          columns: b.columns.filter((c) => c.field_id.trim()).map(cleanColumn),
          max_rows: b.max_rows,
          show_row_numbers: b.show_row_numbers ?? false,
        }
        if (b.order_by?.trim()) out.order_by = b.order_by.trim()
        return out
      }
      case 'progress_checklist':
        return {
          type: 'progress_checklist',
          steps: b.steps
            .filter((s) => s.label.trim() && s.jql_template.trim())
            .map((s) => ({
              label: s.label.trim(),
              jql_template: s.jql_template.trim(),
              done_categories: s.done_categories.length ? s.done_categories : ['done'],
            })),
        }
      case 'custom_html':
        return { type: 'custom_html', html: b.html }
      case 'slack_dm_button':
        return {
          type: 'slack_dm_button',
          label: b.label.trim(),
          default_message: b.default_message.trim(),
          issue_type_contains: b.issue_type_contains.trim(),
          preselect_reporter: b.preselect_reporter,
        }
      case 'fin_renewal_button':
        return {
          type: 'fin_renewal_button',
          scenario: b.scenario,
          label: b.label.trim(),
          issue_type_contains: b.issue_type_contains.trim(),
          // `?? defaults`: a config saved before dom_auto existed has no such keys.
          fin_organizations: (b.fin_organizations ?? ['Boosta'])
            .map((o) => o.trim())
            .filter(Boolean),
          finorg_label: (b.finorg_label ?? '').trim() || 'Створити FINORG Renewal',
          organization_field_id:
            (b.organization_field_id ?? '').trim() || 'customfield_10286',
        }
      case 'create_issue_button':
        return {
          type: 'create_issue_button',
          label: b.label.trim(),
          issue_types: b.issue_types.map((t) => t.trim()).filter(Boolean),
          target_project_id: b.target_project_id.trim(),
          target_issue_type_id: b.target_issue_type_id.trim(),
          fields: b.fields.map(cleanRule),
          link_type_id: b.link_type_id.trim(),
          link_direction: b.link_direction,
        }
      case 'bamboo_profile':
        return { type: 'bamboo_profile', show: [...b.show], fields: [...b.fields] }
    }
  })
}

export type BlockError = { index: number; message: string }

/** First blocking validation problem, or null. Mirrors the backend constraints. */
// Upper bounds mirror the backend Pydantic max_length caps (panels/blocks.py). Enforced
// here so exceeding them shows an inline error instead of a cryptic 422 on save.
const MAX_BLOCKS = 20
const MAX_FIELDS = 30
const MAX_COLUMNS = 10
const MAX_STEPS = 30
const MAX_CREATE_RULES = 30
const MAX_CREATE_ISSUE_TYPES = 20
const FIELD_ID_RE = /^[a-zA-Z0-9_.:-]+$/

export function validatePanelBlocks(blocks: Block[]): BlockError | null {
  if (blocks.length === 0) return { index: -1, message: 'Add at least one block.' }
  if (blocks.length > MAX_BLOCKS)
    return { index: -1, message: `A panel can have at most ${MAX_BLOCKS} blocks.` }
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]
    switch (b.type) {
      case 'heading':
        if (!b.text.trim()) return { index: i, message: 'Heading text is required.' }
        break
      case 'issue_fields': {
        const filled = b.fields.filter((f) => f.field_id.trim())
        if (filled.length === 0) return { index: i, message: 'Add at least one field with an id.' }
        if (b.fields.length > MAX_FIELDS)
          return { index: i, message: `A block can have at most ${MAX_FIELDS} fields.` }
        if (filled.some((f) => f.source === 'child' && !f.aggregate))
          return { index: i, message: 'Child fields need an aggregate.' }
        break
      }
      case 'jql_table':
        if (!b.jql_template.trim()) return { index: i, message: 'JQL template is required.' }
        if (b.jql_template.includes("'")) return { index: i, message: 'Single quotes are not allowed in JQL.' }
        if (!b.columns.some((c) => c.field_id.trim())) return { index: i, message: 'Add at least one column.' }
        if (b.columns.length > MAX_COLUMNS)
          return { index: i, message: `A table can have at most ${MAX_COLUMNS} columns.` }
        if (!Number.isInteger(b.max_rows) || b.max_rows < 1 || b.max_rows > 100)
          return { index: i, message: 'Max rows must be between 1 and 100.' }
        break
      case 'progress_checklist':
        if (!b.steps.some((s) => s.label.trim() && s.jql_template.trim()))
          return { index: i, message: 'Add at least one step with a label and JQL.' }
        if (b.steps.length > MAX_STEPS)
          return { index: i, message: `A checklist can have at most ${MAX_STEPS} steps.` }
        break
      case 'custom_html':
        if (!b.html.trim()) return { index: i, message: 'HTML is required.' }
        break
      case 'slack_dm_button':
        if (!b.label.trim()) return { index: i, message: 'Label is required.' }
        break
      case 'fin_renewal_button':
        if (!b.label.trim()) return { index: i, message: 'Label is required.' }
        if (b.scenario === 'dom_auto') {
          const fieldId = (b.organization_field_id ?? '').trim()
          if (fieldId && !/^[a-zA-Z0-9_]+$/.test(fieldId))
            return { index: i, message: 'Organization field id must be a plain field id.' }
        }
        break
      case 'create_issue_button': {
        if (!b.label.trim()) return { index: i, message: 'Label is required.' }
        if (b.label.trim().length > 60) return { index: i, message: 'Label is limited to 60 characters.' }
        if (b.issue_types.filter((t) => t.trim()).length === 0)
          return { index: i, message: 'Pick at least one issue type to show the button on.' }
        if (b.issue_types.length > MAX_CREATE_ISSUE_TYPES)
          return { index: i, message: `At most ${MAX_CREATE_ISSUE_TYPES} issue types.` }
        if (!/^\d+$/.test(b.target_project_id.trim()))
          return { index: i, message: 'Target project is required (numeric id).' }
        if (!/^\d+$/.test(b.target_issue_type_id.trim()))
          return { index: i, message: 'Target issue type is required (numeric id).' }
        if (!/^\d*$/.test(b.link_type_id.trim()))
          return { index: i, message: 'Link type id must be digits (empty = no link).' }
        if (b.fields.length > MAX_CREATE_RULES)
          return { index: i, message: `A block can have at most ${MAX_CREATE_RULES} rules.` }
        const seen = new Set<string>()
        for (let r = 0; r < b.fields.length; r++) {
          const rule = b.fields[r]
          const n = r + 1
          const target = rule.target.trim()
          if (!target) return { index: i, message: `Rule ${n}: target field is required.` }
          if (target.length > 64 || !FIELD_ID_RE.test(target))
            return { index: i, message: `Rule ${n}: target must be a plain field id.` }
          if (seen.has(target)) return { index: i, message: `Rule ${n}: target ${target} is already used.` }
          seen.add(target)
          if ((rule.source === 'copy' || rule.source === 'template') && !rule.from_field.trim())
            return { index: i, message: `Rule ${n}: from field is required.` }
          if (rule.source === 'const') {
            const v = rule.value
            const empty = Array.isArray(v) ? v.filter((x) => x.trim()).length === 0 : !(v ?? '').trim()
            if (empty) return { index: i, message: `Rule ${n}: value is required.` }
            if (Array.isArray(v) && v.length > 20)
              return { index: i, message: `Rule ${n}: at most 20 values.` }
          }
          if (rule.source === 'date_offset' && (!Number.isInteger(rule.days) || rule.days < 0 || rule.days > 30))
            return { index: i, message: `Rule ${n}: days must be between 0 and 30.` }
        }
        break
      }
      case 'bamboo_profile':
        break // enum-constrained checkboxes; min-1 enforced by the form
      case 'divider':
        break
    }
  }
  return null
}
